/**
 * Types and interfaces for TableSearch components
 */

// ============================================================================
// BASIC SEARCH TYPES
// ============================================================================

export interface BasicSearchProps {
  searchTerm: string;
  placeholder?: string;
  onSearchTermChange: (term: string) => void;
  onClearSearch?: () => void;
}

// ============================================================================
// ADVANCED SEARCH TYPES
// ============================================================================

/**
 * Supported data types for advanced search fields
 */
export type FieldDataType = 'string' | 'number' | 'date' | 'selection';

/**
 * Operator types based on field data type
 */
export type StringOperator = 'contains' | 'equals' | 'notEquals';
export type NumberOperator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'lessThan';
export type DateOperator = 'isBefore' | 'isAfter' | 'isBetween' | 'equals';
export type SelectionOperator = 'equals' | 'notEquals';

export type FieldOperator =
  | StringOperator
  | NumberOperator
  | DateOperator
  | SelectionOperator;

/**
 * Logical operator for combining rules/groups
 */
export type LogicalOperator = 'AND' | 'OR';

/**
 * Configuration for a searchable field
 */
export interface FieldConfig {
  /** Unique key for the field (matches database column or API parameter) */
  key: string;
  /** Display label for the field */
  label: string;
  /** Data type of the field */
  dataType: FieldDataType;
  /** Options for selection fields (required if dataType is 'selection') */
  options?: Array<{ label: string; value: string }>;
}

/**
 * A single search rule within a filter group
 */
export interface SearchRule {
  /** Unique identifier for the rule */
  id: string;
  /** Field key from FieldConfig */
  field: string;
  /** Operator for comparison */
  operator: FieldOperator;
  /** Value(s) to compare against */
  value: string | number | Date | [Date, Date];
}

/**
 * A group of search rules combined with a logical operator
 */
export interface FilterGroup {
  /** Unique identifier for the filter group */
  id: string;
  /** Logical operator to combine rules within this group */
  logicalOperator: LogicalOperator;
  /** Array of search rules in this group */
  rules: SearchRule[];
}

/**
 * Complete advanced search query structure
 */
export interface AdvancedSearchQuery {
  /** Array of filter groups */
  filterGroups: FilterGroup[];
  /** Logical operator to combine filter groups */
  groupOperator: LogicalOperator;
}

/**
 * Props for AdvancedSearch component
 */
export interface AdvancedSearchProps {
  /** Configuration defining available fields for searching */
  fieldConfigs: FieldConfig[];
  /** Current advanced search query */
  query: AdvancedSearchQuery;
  /** Callback when search button is clicked */
  onSearch: (query: AdvancedSearchQuery) => void;
  /** Callback when clear button is clicked */
  onClear: () => void;
}

// ============================================================================
// TABLE SEARCH TYPES (Main Component)
// ============================================================================

/**
 * Search mode type
 */
export type SearchMode = 'basic' | 'advanced' | 'basicOnly' | 'advancedOnly';

/**
 * Props for main TableSearch component
 */
export interface TableSearchProps {
  /** Current search mode */
  mode: SearchMode;
  /** Callback when search mode changes */
  onModeChange: (mode: SearchMode) => void;

  // Basic search props
  basicSearchTerm: string;
  onBasicSearchChange: (term: string) => void;
  onBasicSearchClear?: () => void;
  basicPlaceholder?: string;

  // Advanced search props
  advancedFieldConfigs: FieldConfig[];
  advancedQuery: AdvancedSearchQuery;
  onAdvancedSearch: (query: AdvancedSearchQuery) => void;
  onAdvancedClear: () => void;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Operator display labels
 */
export const OPERATOR_LABELS: Record<FieldOperator, string> = {
  // String operators
  contains: 'Contains',
  equals: 'Is Equal To',
  notEquals: 'Is Not Equal To',
  // Number operators
  greaterThan: 'Greater Than',
  lessThan: 'Less Than',
  // Date operators
  isBefore: 'Is Before',
  isAfter: 'Is After',
  isBetween: 'Is Between',
};

/**
 * Get available operators for a given field data type
 */
export function getOperatorsForDataType(
  dataType: FieldDataType
): FieldOperator[] {
  switch (dataType) {
    case 'string':
      return ['contains', 'equals', 'notEquals'];
    case 'number':
      return ['equals', 'notEquals', 'greaterThan', 'lessThan'];
    case 'date':
      return ['isBefore', 'isAfter', 'isBetween', 'equals'];
    case 'selection':
      return ['equals', 'notEquals'];
    default:
      return [];
  }
}

/**
 * Create an empty filter group
 */
export function createEmptyFilterGroup(): FilterGroup {
  return {
    id: `group_${Date.now()}`,
    logicalOperator: 'AND',
    rules: [],
  };
}

/**
 * Create an empty search rule
 */
export function createEmptySearchRule(): SearchRule {
  return {
    id: `rule_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    field: '',
    operator: 'equals',
    value: '',
  };
}

/**
 * Create an empty advanced search query
 */
export function createEmptyAdvancedQuery(): AdvancedSearchQuery {
  return {
    filterGroups: [createEmptyFilterGroup()],
    groupOperator: 'AND',
  };
}
